import { useEffect, useRef } from "react";

const WHATSAPP_URL = "https://wa.me/5598992018216?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20meus%20direitos%20previdenci%C3%A1rios.";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ backgroundColor: "#F2ECD4" }}
    >
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "repeating-linear-gradient(45deg, #0B4D6F 0, #0B4D6F 1px, transparent 0, transparent 50%)",
          backgroundSize: "20px 20px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full py-24 md:py-0" style={{ paddingTop: "100px" }}>
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 min-h-screen lg:min-h-0 lg:py-24">
          {/* Left: Text Content */}
          <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
            <div
              className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-6"
              style={{
                borderColor: "#0B4D6F",
                color: "#0B4D6F",
                fontFamily: "Montserrat, sans-serif",
                opacity: 0,
                animation: "fadeInUp 0.6s ease forwards 0.1s",
              }}
            >
              <i className="ri-shield-check-line"></i>
              Especialista INSS &amp; Benefícios Previdenciários
            </div>

            <h1
              className="reveal text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{
                fontFamily: "Cinzel, serif",
                color: "#1A1A1A",
                opacity: 0,
                animation: "fadeInUp 0.6s ease forwards 0.2s",
              }}
            >
              Especialista em<br />
              <span style={{ color: "#0B4D6F" }}>Benefícios do INSS</span>
            </h1>

            <p
              className="reveal text-lg md:text-xl leading-relaxed mb-8 max-w-xl"
              style={{
                fontFamily: "Montserrat, sans-serif",
                color: "#4A4A4A",
                opacity: 0,
                animation: "fadeInUp 0.6s ease forwards 0.35s",
              }}
            >
              Ajudamos você a conquistar sua aposentadoria ou benefício,
              mesmo com poucos documentos. Atendimento simples, humanizado e sem burocracia.
            </p>

            <div
              className="reveal flex flex-col sm:flex-row items-center gap-4 mb-6"
              style={{ opacity: 0, animation: "fadeInUp 0.6s ease forwards 0.5s" }}
            >
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-3 px-8 py-4 rounded-full text-white font-semibold text-base whitespace-nowrap cursor-pointer transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5"
                style={{
                  backgroundColor: "#0B4D6F",
                  fontFamily: "Montserrat, sans-serif",
                  boxShadow: "0 4px 20px rgba(11,77,111,0.3)",
                }}
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Falar no WhatsApp
              </a>
              <a
                href="#servicos"
                onClick={(e) => { e.preventDefault(); document.querySelector("#servicos")?.scrollIntoView({ behavior: "smooth" }); }}
                className="flex items-center gap-2 px-6 py-4 rounded-full font-semibold text-base whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-white/60"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  color: "#0B4D6F",
                  border: "2px solid #0B4D6F",
                }}
              >
                Ver Serviços
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <p
              className="reveal text-sm"
              style={{
                fontFamily: "Montserrat, sans-serif",
                color: "#888",
                opacity: 0,
                animation: "fadeInUp 0.6s ease forwards 0.6s",
              }}
            >
              <i className="ri-time-line mr-1"></i>
              Atendimento rápido e humanizado · Online e Presencial
            </p>

            {/* Stats */}
            <div
              className="reveal flex flex-wrap justify-center lg:justify-start gap-8 mt-10 pt-8 border-t w-full"
              style={{
                borderColor: "rgba(11,77,111,0.2)",
                opacity: 0,
                animation: "fadeInUp 0.6s ease forwards 0.7s",
              }}
            >
              {[
                { value: "+15", label: "Anos de Experiência" },
                { value: "+500", label: "Casos Aprovados" },
                { value: "100%", label: "Dedicação ao Cliente" },
              ].map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div
                    className="text-3xl font-bold"
                    style={{ fontFamily: "Cinzel, serif", color: "#0B4D6F" }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs mt-1"
                    style={{ fontFamily: "Montserrat, sans-serif", color: "#666" }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image */}
          <div
            className="reveal flex-1 w-full max-w-lg lg:max-w-none"
            style={{ opacity: 0, animation: "fadeInUp 0.8s ease forwards 0.3s" }}
          >
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ height: "520px", maxHeight: "70vh" }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/4f7051cf-de8d-470e-be57-4cb438d07309/87fa8d51-f605-4086-9b34-a4a2377dab1b_jordanna-rates---hero.jpg?v=678179c04111f3b85a713cb388986efd"
                alt="Dra. Jordanna Rates - Advogada Previdenciária especialista em INSS"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(11,77,111,0.15))" }} />

              {/* Floating badge */}
              <div
                className="absolute bottom-6 left-6 right-6 rounded-2xl p-4 flex items-center gap-3"
                style={{ backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)" }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#0B4D6F" }}
                >
                  <i className="ri-award-line text-white text-xl"></i>
                </div>
                <div>
                  <div
                    className="font-semibold text-sm"
                    style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
                  >
                    Dra. Jordanna Rates
                  </div>
                  <div
                    className="text-xs"
                    style={{ fontFamily: "Montserrat, sans-serif", color: "#666" }}
                  >
                    Advogada Previdenciária · OAB/MA 10.703
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce text-center w-full px-4 md:w-auto md:px-0">
        <span className="text-xs block text-center" style={{ fontFamily: "Montserrat, sans-serif", color: "#888" }}>Role para baixo</span>
        <i className="ri-arrow-down-line block text-center" style={{ color: "#0B4D6F" }}></i>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
